/**
 * Formats an ISO string to a relative time string (e.g., "5 min ago").
 * @param isoString The ISO date string to format.
 * @returns A human-readable relative time string.
 */
export const formatRelativeTime = (isoString: string): string => {
  if (!isoString) return "never";
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  } catch {
    return "recently";
  }
};

/**
 * Returns a formatted label for a given sync status.
 * @param status The sync status.
 * @returns A string including an emoji and the status name.
 */
export const getStatusLabel = (status: string): string => {
  switch (status) {
    case "synced":
      return "🟢 Synced";
    case "not_synced":
      return "🟡 Pending";
    case "error":
      return "🔴 Error";
    case "conflict":
      return "⚠️ Conflict";
    default:
      return status || "Unknown";
  }
};

/**
 * Formats an ISO string to a localized date and time string.
 * @param isoString The ISO date string to format.
 * @returns A localized date and time string.
 */
export const formatTime = (isoString: string): string => {
  if (!isoString) return "—";
  try {
    const date = new Date(isoString);
    return date.toLocaleString();
  } catch {
    return isoString;
  }
};

/**
 * Formats an ISO string to a localized time string.
 * @param isoString The ISO date string to format.
 * @returns A localized time string.
 */
export const formatTimeString = (isoString: string): string => {
  if (!isoString) return "—";
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString();
  } catch {
    return isoString;
  }
};
